import Link from 'next/link';

import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';
import { StepIcon } from '@poseidon/components/StepIcon';

import type { SendsTodayEntry, TodayMeterState } from '~/components/SendsTodayStrip.utils';
import { SEND_EVENT_LABELS } from '~/components/SendsTodayStrip.utils';

const STATE_TEXT: Record<TodayMeterState, string> = {
  normal: 'text-slate-500 dark:text-slate-400',
  capped: 'text-amber-600 dark:text-amber-400',
  quiet: 'text-amber-600 dark:text-amber-400',
};

function meterTooltip(entries: SendsTodayEntry[], state: TodayMeterState): string {
  const lines = entries.map(
    (e) =>
      `${SEND_EVENT_LABELS[e.event]}: ${e.sent}${e.cap > 0 ? ` of ${e.cap}` : ''}${e.errors > 0 ? ` (${e.errors} failed)` : ''}`,
  );
  if (state === 'capped') lines.push('A daily limit is used up. Sending resumes at midnight.');
  if (state === 'quiet') lines.push('Nothing has gone out today, though people are enrolled. Check your campaigns.');
  return `Sent today · ${lines.join(' · ')}`;
}

/**
 * Sidebar "Today" meter: the current person's sends today against their daily caps. Muted
 * normally; amber when a cap is hit or a weekday afternoon has gone by with nothing sent.
 * Pure: `LeftNav` fetches the data and works out `state`.
 */
export function TodaySendsMeter({
  entries,
  state,
  minimized,
}: {
  entries: SendsTodayEntry[];
  state: TodayMeterState;
  minimized: boolean;
}) {
  if (entries.length === 0) return null;
  const alert = state !== 'normal';
  return (
    <Tooltip tooltip={meterTooltip(entries, state)} className="block">
      <Link
        href="/templates"
        data-testid="today-sends-meter"
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800',
          minimized && 'justify-center',
        )}>
        <span
          className={cn('size-1.5 shrink-0 rounded-full', alert ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600')}
        />
        {!minimized && (
          <span className={cn('flex min-w-0 items-center gap-2 text-xs tabular-nums', STATE_TEXT[state])}>
            <span className="text-2xs font-semibold uppercase tracking-wide">Today</span>
            {entries.map((e) => (
              <span key={e.event} className="flex items-center gap-1">
                <StepIcon iconType={e.event} className="size-3" />
                <span>
                  {e.sent}
                  {e.cap > 0 && <span className="opacity-60">/{e.cap}</span>}
                </span>
              </span>
            ))}
          </span>
        )}
      </Link>
    </Tooltip>
  );
}
